import { useEffect, useState } from "react";
import "./_Dashboard.scss";
import "./_DashboardMobile.scss";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

function _Dashboard() {
  const [totalSale, setTotalSale] = useState(0);
  const [orderCount, setOrderCount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [revenueData, setRevenueData] = useState([]);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [reportType, setReportType] = useState("order");
  const [prodType, setProdType] = useState("onhand");
  const formatToJanDDYYYY = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = months[date.getMonth()];
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };
  useEffect(() => {
    const fetchOverview = () => {
      fetch(
        `${process.env.REACT_APP_BASE_URL}api/dashboard/get-dash-overview`,
        {
          credentials: "include",
        }
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setTotalSale(data.totalRevenue);
            setOrderCount(data.orderCount);
            setProductCount(data.productCount);
            setUserCount(data.userCount);
          }
        })
        .catch((err) =>
          console.error("Error fetching dashboard overview:", err)
        );

      // Fetch monthly revenue for graph
      fetch(
        `${process.env.REACT_APP_BASE_URL}api/dashboard/get-monthly-revenue`,
        {
          credentials: "include",
        }
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setRevenueData(data.data);
          }
        })
        .catch((err) => console.error("Error fetching monthly revenue:", err));
    };

    fetchOverview();
  }, []);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    const fetchRecentOrders = () => {
      fetch(`${process.env.REACT_APP_BASE_URL}api/dashboard/recent-orders`, {
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setRecentOrders(data.data);
          }
        })
        .catch((err) => console.error("Error fetching recent orders:", err));
    };

    fetchRecentOrders();
  }, []);

  const exportReport = async () => {
    // 🔴 STOP if dateFrom or dateTo is empty
    if (!dateFrom || !dateTo) {
      return alert("Please select both FROM and TO dates.");
    }

    const res = await fetch(
      `${process.env.REACT_APP_BASE_URL}api/dashboard/generate-report?from=${dateFrom}&to=${dateTo}&type=${reportType}&prodtype=${prodType}`,
      { credentials: "include" }
    );

    const data = await res.json();
    if (!data.success) return alert("No data found");

    const headers = data.headers;
    const rows = data.rows.map((obj) => Object.values(obj));

    // 1. Create workbook & worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(reportType.toUpperCase());

    // 2. Add header row
    const headerRow = worksheet.addRow(headers);

    // 3. Style headers
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; // white text
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF4F81BD" }, // blue background
      };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    // 4. Add data rows with proper date conversion
    rows.forEach((row) => {
      const newRow = [...row];
      const jsDate = new Date(newRow[0]);
      if (!isNaN(jsDate)) newRow[0] = jsDate; // convert first column to Date
      worksheet.addRow(newRow);
    });

    // 5. Format date column (first column) as Jan-02-2025
    const dateColumnIndex = 1; // ExcelJS is 1-based
    worksheet.getColumn(dateColumnIndex).numFmt = "mmm-dd-yyyy";

    // 6. Auto filter
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: headers.length },
    };

    // 7. Auto width with proper date handling
    worksheet.columns.forEach((column, colIndex) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        let cellValue = "";

        if (cell.value instanceof Date) {
          // Format date as mmm-dd-yyyy for width calculation
          const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          const month = monthNames[cell.value.getMonth()];
          const day = String(cell.value.getDate()).padStart(2, "0");
          const year = cell.value.getFullYear();
          cellValue = `${month}-${day}-${year}`;
        } else if (cell.value != null) {
          cellValue = cell.value.toString();
        }

        maxLength = Math.max(maxLength, cellValue.length);
      });

      column.width = maxLength + 2; // add some padding
    });

    // 8. Write to file and download
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      reportType === "user"
        ? `${reportType}_report.xlsx`
        : `${reportType}_${prodType}_${formatToJanDDYYYY(
            dateFrom
          )}_${formatToJanDDYYYY(dateTo)}_report.xlsx`
    );
  };

  return (
    <>
      <main className="admin-dashboard-container">
        <div className="left-panel">
          <div className="overview">
            <div className="overview-datails">
              <span className="title">SALES</span>
              <span className="amount">PHP {totalSale}</span>
            </div>

            <div className="overview-datails">
              <span className="title">ORDER </span>
              <span className="amount">{orderCount} Items</span>
            </div>

            <div className="overview-datails">
              <span className="title">PRODUCT</span>
              <span className="amount">{productCount} Items</span>
            </div>

            <div className="overview-datails">
              <span className="title">USER</span>
              <span className="amount">{userCount} customer</span>
            </div>
          </div>

          <div className="reports">
            <div className="generate">
              <span className="title">GENERATE REPORT</span>

              <div className="select-month-wrapper">
                <div className="month-input-wrapper">
                  <span>FROM:</span>
                  <input
                    type="date"
                    value={dateFrom || ""}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="month-input-wrapper">
                  <span>TO:</span>
                  <input
                    type="date"
                    value={dateTo || ""}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>

              <div className="report-type-wrapper">
                <div className="input-wrapper">
                  <span>REPORT TYPE:</span>
                  <select
                    value={reportType || ""}
                    onChange={(e) => {
                      if (e.target.value === "order") {
                        setProdType("onhand");
                      } else if (e.target.value === "product") {
                        setProdType("all");
                      }
                      setReportType(e.target.value);
                    }}
                  >
                    <option value="order">ORDER</option>
                    <option value="product">PRODUCT</option>
                    <option value="user">USER</option>
                  </select>
                </div>
              </div>
              {reportType === "order" ? (
                <div className="report-type-wrapper">
                  <div className="input-wrapper">
                    <span>PRODUCT TYPE:</span>
                    <select
                      value={prodType || ""}
                      onChange={(e) => setProdType(e.target.value)}
                    >
                      <option value="onhand">ONHAND</option>
                      <option value="preorder">PREORDER</option>
                    </select>
                  </div>
                </div>
              ) : reportType === "product" ? (
                <div className="report-type-wrapper">
                  <div className="input-wrapper">
                    <span>PRODUCT TYPE:</span>
                    <select
                      value={prodType || ""}
                      onChange={(e) => setProdType(e.target.value)}
                    >
                      <option value="all">ALL</option>
                      <option value="onhand">ONHAND</option>
                      <option value="preorder">PREORDER</option>
                    </select>
                  </div>
                </div>
              ) : (
                ""
              )}
              <button onClick={exportReport}>GENERATE</button>
            </div>

            <div className="graph">
              <span className="title">REVENUE</span>

              <div className="graph-wrapper">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="revenue" // make sure this matches your API field
                      stroke="#000"
                      fill="rgba(0, 0, 0, 0.46)" // optional: lighter fill
                      strokeWidth={3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        <div className="right-panel">
          {" "}
          <div className="recent-orders">
            <div className="recent-orders-datails">
              <span className="title">RECENT ORDERS</span>
              <table>
                <tbody>
                  <tr className="divider">
                    <td colSpan={3}></td>
                  </tr>
                  <tr className="head">
                    <td>ORDER ID</td>
                    <td>STATUS</td>
                    <td>DATE/TIME</td>
                  </tr>
                  {recentOrders.map((order, index) => (
                    <>
                      <tr key={index}>
                        <td>{order.COI_Number}</td>
                        <td>{order.COI_Status}</td>
                        <td className="date">
                          {new Date(order.COI_Created_At).toLocaleString()}
                        </td>
                      </tr>
                      <tr className="divider">
                        <td colSpan={3}></td>
                      </tr>
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default _Dashboard;
